/**
 * Customer-portal modal form for proposing a new student record (OWZ-9 Round 3).
 *
 * Posts to POST /api/customer/students (created Round 1, OWZ-1). The backend
 * creates a `StudentAddSubmission` with `status="pending"` and the admin
 * approves/rejects it via the Round 2 admin endpoints — only on approve does
 * a real `Student` record materialize.
 */
import { useState } from 'react';
import { X, CheckCircle, UserPlus } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';

import useConnectivity from '@/services/connectivityService';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('memberToken') || localStorage.getItem('customerToken')}`,
  'Content-Type': 'application/json',
});

const AddStudentForm = ({ open, onClose, onSubmitted }) => {
  const { isOnline } = useConnectivity();
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [studentDob, setStudentDob] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const reset = () => {
    setStudentName('');
    setStudentEmail('');
    setStudentDob('');
    setNotes('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isOnline) {
      setError('You are offline. Reconnect to submit this request.');
      return;
    }

    if (!studentName.trim()) {
      setError('Student name is required.');
      return;
    }

    setLoading(true);
    try {
      const body = {
        student_name: studentName.trim(),
        student_email: studentEmail.trim() || null,
        student_dob: studentDob || null,
        notes: notes.trim() || null,
      };
      const response = await fetch(`${API_BASE}/api/customer/students`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setSuccess(true);
        onSubmitted?.();
        // Auto-close so the empty-state can refetch the pending list.
        setTimeout(() => handleClose(), 1400);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Could not submit. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full shadow-xl">
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-red-600 dark:text-red-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add a Student</h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <p className="font-medium text-green-800 dark:text-green-300">
              Submitted for review
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              An instructor will approve this and add the student's programs.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Tell us about the student you'd like to add. An instructor will
              review and finalize program enrollment.
            </p>

            <div className="space-y-1">
              <label htmlFor="add-student-name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Student name <span className="text-red-600">*</span>
              </label>
              <Input
                id="add-student-name"
                type="text"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                placeholder="e.g., Brian Williams Jr."
                required
                autoFocus
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="add-student-email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Email <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <Input
                id="add-student-email"
                type="email"
                value={studentEmail}
                onChange={(e) => setStudentEmail(e.target.value)}
                placeholder="student@example.com"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Lets the student sign in independently later.
              </p>
            </div>

            <div className="space-y-1">
              <label htmlFor="add-student-dob" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Date of birth <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <Input
                id="add-student-dob"
                type="date"
                value={studentDob}
                onChange={(e) => setStudentDob(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="add-student-notes" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Notes for the instructor <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                id="add-student-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any context that would help us set this student up — prior experience, sibling of an existing student, etc."
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>
            )}

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? 'Submitting...' : 'Submit for review'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddStudentForm;
